import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { readClientSession } from "utils/clientSession";

function Label({ children }) {
  return (
    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {children}
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className={`h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-navy-700 outline-none transition focus:border-brand-400 focus:ring-2 focus:ring-brand-200/60 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:focus:border-brand-300 dark:focus:ring-brand-400/30 ${
        props.className || ""
      }`}
    />
  );
}

export default function ClientSettings() {
  const session = readClientSession();

  const [profile, setProfile] = useState({
    username: session?.email || "username_locked",
    clientId: session?.clientId || "",
    firstName: session?.firstName || "",
    lastName: session?.lastName || "",
    country: session?.country || "",
    city: "",
    phone: "",
    email: session?.email || "",
  });

  const completion = useMemo(() => {
    const fields = [
      profile.username,
      profile.clientId,
      profile.firstName,
      profile.lastName,
      profile.country,
      profile.city,
      profile.phone,
      profile.email,
    ];
    const filled = fields.filter((v) => String(v || "").trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [profile]);

  return (
    <div className="space-y-6">
      <div className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
              Client Settings
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Manage your profile, verification methods, account security, and
              account lifecycle options.
            </p>
          </div>
          <span className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/15 dark:text-brand-200">
            Profile Completion: {completion}%
          </span>
        </div>
      </div>

      <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">Profile</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Personal details used for your client identity and account presentation.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>User (Unchangeable)</Label>
            <Input
              value={profile.username}
              disabled
              className="opacity-80"
            />
          </div>
          <div>
            <Label>Client ID (Read-only)</Label>
            <Input value={profile.clientId} disabled className="opacity-80" />
          </div>
          <div>
            <Label>First Name</Label>
            <Input
              value={profile.firstName}
              onChange={(e) =>
                setProfile((p) => ({ ...p, firstName: e.target.value }))
              }
            />
          </div>
          <div>
            <Label>Last Name</Label>
            <Input
              value={profile.lastName}
              onChange={(e) =>
                setProfile((p) => ({ ...p, lastName: e.target.value }))
              }
            />
          </div>
          <div>
            <Label>Country (Selected at Sign-up, Unchangeable)</Label>
            <Input
              value={profile.country}
              disabled
              className="opacity-80"
              placeholder="Locked from sign-up"
            />
          </div>
          <div>
            <Label>City</Label>
            <Input
              value={profile.city}
              onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))}
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={profile.phone}
              onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
              placeholder="+213..."
            />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input value={profile.email} disabled className="opacity-80" />
          </div>
        </div>
        <button
          type="button"
          className="mt-5 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 dark:bg-brand-400 dark:text-navy-900 dark:hover:bg-brand-300"
        >
          Save Profile
        </button>
      </section>

      <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          Verification
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Manage your email verification and Telegram identity verification.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
              Email Verification Status
            </p>
            <p className="mt-1 text-sm text-emerald-600 dark:text-emerald-400">
              Verified
            </p>
            <button
              type="button"
              className="mt-3 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
            >
              Resend Verification Email
            </button>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
              Telegram Linked
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              @not_connected
            </p>
            <button
              type="button"
              className="mt-3 rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 transition hover:bg-brand-100 dark:border-brand-500/40 dark:bg-brand-500/15 dark:text-brand-200 dark:hover:bg-brand-500/25"
            >
              Connect Telegram (Bot + OTP)
            </button>
          </div>
          <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 text-sm text-gray-600 dark:border-white/20 dark:text-gray-400">
            Last verification attempt: Not available yet.
          </div>
        </div>
      </section>

      <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">Security</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Password controls, 2FA settings, and active session monitoring.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Current Password</Label>
            <Input type="password" placeholder="••••••••" />
          </div>
          <div>
            <Label>New Password</Label>
            <Input type="password" placeholder="Minimum 8 characters" />
          </div>
          <div className="md:col-span-2">
            <Label>Confirm New Password</Label>
            <Input type="password" placeholder="Repeat your new password" />
          </div>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
              2FA Status
            </p>
            <p className="mt-1 text-sm text-amber-600 dark:text-amber-400">Off</p>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Method: Authenticator app (recommended) + backup codes.
            </p>
            <button
              type="button"
              className="mt-3 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
            >
              Enable 2FA
            </button>
          </div>
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            <p className="text-sm font-semibold text-navy-700 dark:text-white">
              Active Sessions
            </p>
            <ul className="mt-2 space-y-2 text-xs text-gray-600 dark:text-gray-400">
              <li>IP: 127.0.0.1 - Device: Chrome - Last seen: Just now</li>
              <li>IP: 41.x.x.x - Device: Mobile - Last seen: 2 hours ago</li>
            </ul>
            <button
              type="button"
              className="mt-3 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50 dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-500/15"
            >
              Log Out Other Sessions
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-[20px] border border-red-200 bg-red-50 p-6 shadow-sm dark:border-red-500/30 dark:bg-red-500/10">
        <h3 className="text-lg font-bold text-red-800 dark:text-red-300">
          Danger Zone
        </h3>
        <p className="mt-2 text-sm text-red-700/90 dark:text-red-200/90">
          High-impact actions for temporary account deactivation or permanent
          account deletion.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            className="rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 dark:border-red-500/40 dark:bg-red-500/5 dark:text-red-300 dark:hover:bg-red-500/20"
          >
            Deactivate Account Temporarily
          </button>
          <button
            type="button"
            className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400"
          >
            Delete Account (Password + Confirmation)
          </button>
        </div>
      </section>

      <Link
        to="/client/dashboard"
        className="inline-block text-sm font-medium text-brand-500 hover:underline dark:text-brand-300"
      >
        ← Back to Dashboard
      </Link>
    </div>
  );
}
